import Two from 'two.js';
import { WHEEL_RADIUS } from 'rf-ui/GameRenderer/wheelOfFortuneConstants';

const COLOR_NAME_FILL = '#FFFFFF';
const NAME_TAG_TEXTURE_SUPERSAMPLE = 3;
const BASE_NAME_TAG_FONT_SIZE = WHEEL_RADIUS * 0.13;

export type CachedNameTexture = {
  texture: Two.Texture;
  width: number;
  height: number;
  fontSize: number;
};

export default class TextTextureFactory {
  private static readonly nameTextureCache = new WeakMap<Two, Map<string, CachedNameTexture>>();

  private static getCacheKey(name: string, family: string) {
    return `${family}::${name}`;
  }

  private static getRendererCache(two: Two) {
    const existingCache = TextTextureFactory.nameTextureCache.get(two);
    if (existingCache) {
      return existingCache;
    }

    const newCache = new Map<string, CachedNameTexture>();
    TextTextureFactory.nameTextureCache.set(two, newCache);
    return newCache;
  }

  static getOrCreateNameTexture(two: Two, name: string, family: string): CachedNameTexture {
    const rendererCache = TextTextureFactory.getRendererCache(two);
    const cacheKey = TextTextureFactory.getCacheKey(name, family);
    const cached = rendererCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const font = `${BASE_NAME_TAG_FONT_SIZE}px ${family}`;
    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d');
    if (!measureCtx) {
      throw Error('No canvas context');
    }

    measureCtx.font = font;
    const textWidth = Math.max(1, Math.ceil(measureCtx.measureText(name).width));
    const textHeight = Math.max(1, Math.ceil(BASE_NAME_TAG_FONT_SIZE * 1.4));
    const padding = Math.ceil(BASE_NAME_TAG_FONT_SIZE * 0.2);
    const width = textWidth + (padding * 2);
    const height = textHeight + (padding * 2);
    const scale = Math.max(1, Math.floor(window.devicePixelRatio || 1)) * NAME_TAG_TEXTURE_SUPERSAMPLE;

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(width * scale);
    canvas.height = Math.ceil(height * scale);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw Error('No 2d context');
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = COLOR_NAME_FILL;
    ctx.fillText(name, width / 2, height / 2);

    const texture = new Two.Texture(canvas);
    texture.repeat = 'no-repeat';
    texture.scale = 1 / scale;

    const cacheEntry: CachedNameTexture = {
      texture,
      width,
      height,
      fontSize: BASE_NAME_TAG_FONT_SIZE,
    };

    rendererCache.set(cacheKey, cacheEntry);

    return cacheEntry;
  }
}
